import AxiosController from "../authentication/AxiosController";
import {
  guardSaSkuOrder,
  isSaOrderableSku,
  notifySaSkuUnavailable,
} from "../globals/functions/saAllowedSkus";
import { canBypassSaSkuForExclusiveProduct, getUserExclusiveProductIds } from "../globals/functions/saExclusiveOrder";

const resolveProductSku = async ({
  slug,
  lang,
  token,
  productId,
  variantId,
  sku,
  exclusiveParam,
}) => {
  if (sku) return sku;

  const res = await AxiosController.get(
    `api/${slug}/${lang}/get-product/${productId}`,
    {
      headers: { Authorization: `Bearer ${token}` },
      params: exclusiveParam ? { exclusive: exclusiveParam } : {},
    }
  );

  const variants = res.data?.product_variants || [];
  if (variantId) {
    return variants.find((variant) => String(variant.id) === String(variantId))
      ?.sku;
  }

  return variants[0]?.sku;
};

const resolveVariantSku = async ({ slug, lang, token, variantId, sku }) => {
  if (sku) return sku;

  const res = await AxiosController.get(
    `api/${slug}/${lang}/get-variant-details/${variantId}`,
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );

  return res.data?.sku;
};

export const ensureSaSkuOrderable = async ({
  slug,
  lang,
  token,
  productId,
  variantId,
  sku,
  exclusiveParam,
}) => {
  if (slug !== "sa") return true;

  if (
    productId &&
    (await canBypassSaSkuForExclusiveProduct({
      slug,
      lang,
      token,
      productId,
      exclusiveParam,
    }))
  ) {
    return true;
  }

  try {
    const resolvedSku = variantId
      ? await resolveVariantSku({ slug, lang, token, variantId, sku })
      : await resolveProductSku({
          slug,
          lang,
          token,
          productId,
          variantId,
          sku,
          exclusiveParam,
        });

    return guardSaSkuOrder(slug, resolvedSku);
  } catch {
    notifySaSkuUnavailable();
    return false;
  }
};

export const ensureSaCartOrderable = async ({
  slug,
  lang,
  token,
  cartItems,
  exclusiveParam,
}) => {
  if (slug !== "sa" || !cartItems?.length) return true;

  const exclusiveProductIds = await getUserExclusiveProductIds(slug, lang, token);

  for (const item of cartItems) {
    const isExclusiveItem =
      exclusiveParam === "1" ||
      (item.p_id && exclusiveProductIds.includes(String(item.p_id)));

    if (isExclusiveItem) {
      continue;
    }

    const canOrder = await ensureSaSkuOrderable({
      slug,
      lang,
      token,
      productId: item.p_id,
      variantId: item.v_id,
      sku: item.sku,
      exclusiveParam,
    });

    if (!canOrder) return false;
  }

  return true;
};

export const addProductToCart = async ({
  slug,
  lang,
  token,
  productId,
  variantId,
  sku,
  exclusiveParam,
}) => {
  const canOrder = await ensureSaSkuOrderable({
    slug,
    lang,
    token,
    productId,
    variantId,
    sku,
    exclusiveParam,
  });

  if (!canOrder) {
    throw new Error("SKU not orderable");
  }

  const url = variantId
    ? `api/${slug}/${lang}/add-to-cart/${productId}/${variantId}`
    : `api/${slug}/${lang}/add-to-cart/${productId}`;

  return AxiosController.post(
    url,
    {},
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );
};

export const addProductDiscountToCart = async ({
  slug,
  lang,
  token,
  offerId,
}) => {
  if (slug === "sa") {
    const cartRes = await AxiosController.get(`api/${slug}/${lang}/get-cart`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    const currentCart = cartRes.data || [];
    const response = await AxiosController.post(
      `apiv2/${slug}/${lang}/product-discounts/add-to-cart/${offerId}`,
      {},
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );

    const updatedCartRes = await AxiosController.get(
      `api/${slug}/${lang}/get-cart`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    const updatedCart = updatedCartRes.data || [];
    const newItems = updatedCart.filter(
      (item) => !currentCart.some((currentItem) => currentItem.id === item.id)
    );

    if (newItems.length) {
      const canOrder = await ensureSaCartOrderable({
        slug,
        lang,
        token,
        cartItems: newItems,
      });

      if (!canOrder) {
        await Promise.all(
          newItems.map((item) =>
            AxiosController.put(
              `api/${slug}/${lang}/delete-cart/`,
              { cartId: item.id, productId: item.p_id },
              {
                headers: { Authorization: `Bearer ${token}` },
              }
            )
          )
        );
        throw new Error("SKU not orderable");
      }
    }

    return response;
  }

  return AxiosController.post(
    `apiv2/${slug}/${lang}/product-discounts/add-to-cart/${offerId}`,
    {},
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );
};

export const addMarketerOfferToCart = async ({
  slug,
  lang,
  token,
  offerId,
}) => {
  if (slug === "sa") {
    const cartRes = await AxiosController.get(`api/${slug}/${lang}/get-cart`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    const currentCart = cartRes.data || [];
    const response = await AxiosController.post(
      `apiv2/${slug}/${lang}/offers/add-to-cart/${offerId}`,
      {},
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );

    const updatedCartRes = await AxiosController.get(
      `api/${slug}/${lang}/get-cart`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    const updatedCart = updatedCartRes.data || [];
    const newItems = updatedCart.filter(
      (item) => !currentCart.some((currentItem) => currentItem.id === item.id)
    );

    if (newItems.length) {
      const canOrder = await ensureSaCartOrderable({
        slug,
        lang,
        token,
        cartItems: newItems,
      });

      if (!canOrder) {
        await Promise.all(
          newItems.map((item) =>
            AxiosController.put(
              `api/${slug}/${lang}/delete-cart/`,
              { cartId: item.id, productId: item.p_id },
              {
                headers: { Authorization: `Bearer ${token}` },
              }
            )
          )
        );
        throw new Error("SKU not orderable");
      }
    }

    return response;
  }

  return AxiosController.post(
    `apiv2/${slug}/${lang}/offers/add-to-cart/${offerId}`,
    {},
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );
};

export { isSaOrderableSku };
